<script setup lang="ts">
const DURATION = 6000;

const { notification } = storeToRefs(usePromocodeStore());
const { closeNotification } = usePromocodeStore();

const progress = ref(100);

let timer: ReturnType<typeof setInterval> | null = null;

function clearTimer() {
  if (timer) {
    clearInterval(timer);
    timer = null;
  }
}

function startTimer() {
  clearTimer();
  progress.value = 100;

  const step = 100 / (DURATION / 100);

  timer = setInterval(() => {
    progress.value -= step;

    if (progress.value <= 0) {
      clearTimer();
      closeNotification();
    }
  }, 100);
}

// observa título/mensagem além de is_open: dois resgates seguidos mantêm
// is_open === true, então sem isso o timer/progresso não reiniciaria
watch(
  () => [
    notification.value.is_open,
    notification.value.title,
    notification.value.message,
  ],
  () => {
    if (notification.value.is_open) {
      startTimer();
    } else {
      clearTimer();
    }
  },
);

onUnmounted(clearTimer);
</script>

<template>
  <Teleport to="body">
    <div
      aria-live="polite"
      class="transition"
      :class="[
        $style.balloon,
        $style[notification.type],
        { [$style.isOpen]: notification.is_open },
      ]"
      role="status"
    >
      <div :class="[$style.icon, $style[notification.type]]">
        <NuxtIcon
          aria-hidden="true"
          :name="`toaster/${notification.type}`"
        />
      </div>

      <div :class="$style.content">
        <p :class="$style.title">{{ notification.title }}</p>
        <p
          v-if="notification.message"
          :class="$style.message"
        >
          {{ notification.message }}
        </p>
      </div>

      <button
        aria-label="Fechar"
        :class="$style.close"
        type="button"
        @click="closeNotification"
      >
        <NuxtIcon name="close" />
      </button>

      <div :class="$style.progressBar">
        <div
          :class="[$style.progressBarTimer, $style[notification.type]]"
          :style="{ width: `${progress}%` }"
        />
      </div>
    </div>
  </Teleport>
</template>

<style module lang="scss">
.balloon {
  @apply fixed top-4 right-4 z-50 flex items-center gap-3 max-w-[92vw] md:max-w-[420px] pl-3 pr-2.5 py-3 rounded-2xl bg-[#0B1020]/95 backdrop-blur-md border border-white/10 shadow-2xl select-none opacity-0 invisible pointer-events-none overflow-hidden;

  transform: translateX(calc(100% + 1rem)) scale(0.98);
  transition:
    opacity 0.25s ease,
    transform 0.3s cubic-bezier(0.18, 0.89, 0.32, 1.28) !important;

  &.isOpen {
    @apply opacity-100 visible pointer-events-auto;
    z-index: 9999999999;
    transform: translateX(0) scale(1);
  }

  .icon {
    @apply flex items-center justify-center shrink-0 w-9 h-9 rounded-full text-lg;

    &.success {
      @apply text-success bg-success/15;
    }
    &.error {
      @apply text-error bg-error/15;
    }
    &.warning {
      @apply text-warning bg-warning/15;
    }
  }

  .content {
    @apply flex flex-col gap-0.5 min-w-0;

    .title {
      @apply text-white text-sm font-bold leading-tight;
    }

    .message {
      @apply text-white/60 text-xs font-medium leading-snug;
    }
  }

  .close {
    @apply flex items-center justify-center shrink-0 w-6 h-6 ml-1 rounded-full text-white/40 text-sm hover:text-white hover:bg-white/10;
  }

  .progressBar {
    @apply absolute bottom-0 left-0 right-0 h-[3px] bg-white/10;

    .progressBarTimer {
      @apply h-full;
      transition: width 0.1s linear !important;

      &.success {
        @apply bg-success;
      }
      &.error {
        @apply bg-error;
      }
      &.warning {
        @apply bg-warning;
      }
    }
  }
}
</style>
